// e926 searched straight from the visitor's browser. e926 (like e621) turns away every datacenter address, ours included, but answers a
// home or phone address and allows other websites to read its answer (access-control-allow-origin: *). So the visitor's own browser
// asks e926 and we never touch the request. Nothing is sent to our server, and only the visitor's own login (if they saved one) is used.
import type { BooruPost } from "./booru-api"
import type { SourceConfig } from "./source-store"
import { e621TagCategories } from "./e621-tag-types"

const VIDEO_EXT = /\.(webm|mp4|mov|avi|mkv|flv|wmv|m4v|3gp|ogv|gif|gifv)(\?|#|$)/i

function mapRating(rating: unknown): "safe" | "questionable" | "explicit" {
  const r = String(rating ?? "").toLowerCase()
  if (r === "s" || r === "safe" || r === "general") return "safe"
  if (r === "q" || r === "questionable" || r === "sensitive") return "questionable"
  return "explicit"
}

/** Same result as the server's mapE621Posts (lib/booru-sources/booru-api/e621.ts), for posts fetched in the browser. */
export function mapE621ClientPosts(posts: any[], siteId: string): BooruPost[] {
  return posts
    .map((post: any) => {
      const file = post.file || {}
      const preview = post.preview || {}
      const sample = post.sample || {}
      const tags = post.tags || {}
      const score = post.score || {}
      const allTags = [
        ...(tags.general || []),
        ...(tags.species || []),
        ...(tags.character || []),
        ...(tags.copyright || []),
        ...(tags.artist || []),
        ...(tags.invalid || []),
        ...(tags.lore || []),
        ...(tags.meta || []),
      ].filter(Boolean)
      const fileUrl: string = file.url || ""
      const sources = post.sources || []
      const source = Array.isArray(sources) ? sources[0] : sources
      return {
        id: String(post.id),
        fileUrl,
        previewUrl: preview.url || "",
        sampleUrl: sample.url || file.url || "",
        tags: allTags,
        rating: mapRating(post.rating),
        score: score.total || 0,
        source: source || null,
        width: file.width || 0,
        height: file.height || 0,
        site: siteId,
        type: VIDEO_EXT.test(fileUrl) ? "video" : "image",
        tagCategories: e621TagCategories(tags, allTags),
        createdAt: post.created_at,
        rawPost: post,
      } as BooruPost
    })
    .filter((post) => post.fileUrl)
}

export async function fetchE621Client(
  config: SourceConfig,
  params: { tags: string; negativeTags?: string[]; limit?: number; page?: number },
  credentials?: { username?: string; apiKey?: string },
): Promise<BooruPost[]> {
  const url = new URL(config.endpoints.search)
  const wanted = params.tags
    .split(/[,\s]+/)
    .map((t) => t.trim())
    .filter(Boolean)
  const unwanted = (params.negativeTags || []).map((t) => t.trim().replace(/^-/, "")).filter(Boolean).map((t) => `-${t}`)
  const tags = [...wanted, ...unwanted].join(" ")
  if (tags) url.searchParams.set("tags", tags)
  url.searchParams.set(config.limitParam || "limit", String(params.limit || 20))
  url.searchParams.set(config.pagination?.param || "page", String((params.page ?? 0) + (config.pagination?.startAt ?? 1)))
  if (credentials?.username && credentials.apiKey) {
    url.searchParams.set("login", credentials.username)
    url.searchParams.set("api_key", credentials.apiKey)
  }
  // No custom headers on purpose: any would turn this into a pre-flighted request, which e926 refuses.
  const response = await fetch(url.toString(), { method: "GET", mode: "cors", credentials: "omit" })
  if (!response.ok) {
    console.error(`[v0] ${config.name} returned ${response.status} when asked from the browser`)
    return []
  }
  const data = await response.json()
  return mapE621ClientPosts(Array.isArray(data) ? data : data.posts || [], config.id)
}
